import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { IdentityCalendarItem } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';
import { observableInSeparateThread } from '@reg/shared/helper/observable-helper';

/**
 * Service to get and save team calendar events.
 */
@Injectable()
export class IdentityCalendarItemService {
    public static INCLUDE_TEAM_TAGS: string = 'teamTags';
    public static INCLUDE_TEAMS: string = 'teams';

    private identityApiBaseUrl: string;
    private cachedMap: { [apiRequest: string]: IdentityCalendarItem[]; } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    public clearCache(): void {
        this.cachedMap = {};
    }

    /**
     * Get all calendar items for a given team
     */
    public getIndex(
        teamId: string,
        includes: string[] = null,
        queryParams: string[] = null,
        forceRefresh: boolean = false,
    ): Observable<IdentityCalendarItem[]> {
        queryParams = queryParams || [];
        queryParams.push(`teamId=${teamId}`);
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);

        if (!forceRefresh && this.cachedMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedMap[apiRequestUrl].map((a: IdentityCalendarItem) => a.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const calendarItems: IdentityCalendarItem[] = [];
                const included: Object[] = response.included;

                const bodyData: any = response.data || [];
                for (const item of bodyData) {
                    calendarItems.push((new IdentityCalendarItem()).deserialize(item, included));
                }

                this.cachedMap[apiRequestUrl] = calendarItems;
                return calendarItems.map((i: IdentityCalendarItem) => i.clone());
            }));
    }

    /**
     * Get a single calendarItem
     */
    public get(
        identityCalendarItemId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityCalendarItem> {
        const apiRequestUrl: string = this.getApiUrl(identityCalendarItemId, includes, queryParams);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new IdentityCalendarItem().deserialize(response))));
    }

    /**
     * Add or update a calendarItem
     */
    public save(
        calendarItem: IdentityCalendarItem,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityCalendarItem> {
        let response: Observable<IJsonApiResponse>;
        const calendarItemId: string = calendarItem.isNew ? null : calendarItem.id;
        const apiRequestUrl: string = this.getApiUrl(calendarItemId, includes, queryParams);

        if (!calendarItem.isNew) {
            // Update existing CalendarItem
            response = this.http.patch(apiRequestUrl, calendarItem.serialize());
        } else {
            // Add a new CalendarItem
            response = this.http.post(apiRequestUrl, calendarItem.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityCalendarItem()).deserialize(response)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified calendar item
     */
    public delete(calendarItemId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(calendarItemId);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => true),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        calendarItemId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/calendar-items/`;

        if (calendarItemId) {
            url += calendarItemId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}

